import { useMemo } from 'react';

import { SimulationPeriodsProps } from '@graphql/query/simulation';

import usePortfolio from '~/pages/Portfolio/usePortfolio';

const recalculateBalance = (period: SimulationPeriodsProps): SimulationPeriodsProps => ({
  ...period,
  balance: {
    ...period.balance,
    balanceMwm: period.balance.quantityMwm,
  },
});

const useFilteredPeriods = (periods: SimulationPeriodsProps[] | undefined, isPortfolio?: boolean) => {
  const { expandedPeriods } = usePortfolio();

  return useMemo(() => {
    if (!periods) return [];

    if (!isPortfolio) {
      return periods;
    }

    const filtered: SimulationPeriodsProps[] = [];
    const annualPeriodsFromBackend = new Map<string, SimulationPeriodsProps>();

    for (const periodData of periods) {
      const period = periodData.period;
      const isMonthly = period.includes('-');
      const year = isMonthly ? period.split('-')[0] : period;
      const isYearExpanded = expandedPeriods?.includes(year);

      if (isMonthly) {
        if (isYearExpanded) {
          filtered.push(recalculateBalance(periodData));
        }
      } else {
        annualPeriodsFromBackend.set(year, periodData);
      }
    }

    for (const [, annualPeriod] of annualPeriodsFromBackend.entries()) {
      filtered.push(annualPeriod);
    }

    return filtered.sort((a, b) => {
      const aIsMonthly = a.period.includes('-');
      const bIsMonthly = b.period.includes('-');

      if (aIsMonthly && !bIsMonthly) {
        const aYear = a.period.split('-')[0];
        const aMonth = parseInt(a.period.split('-')[1], 10);

        if (aYear === b.period) {
          return aMonth - 13;
        }

        return aYear.localeCompare(b.period);
      }

      if (!aIsMonthly && bIsMonthly) {
        const bYear = b.period.split('-')[0];
        const bMonth = parseInt(b.period.split('-')[1], 10);

        if (a.period === bYear) {
          return 13 - bMonth;
        }

        return a.period.localeCompare(bYear);
      }

      if (aIsMonthly && bIsMonthly) {
        return a.period.localeCompare(b.period);
      }

      return a.period.localeCompare(b.period);
    });
  }, [periods, expandedPeriods, isPortfolio]);
};

export default useFilteredPeriods;
